"use client";
import { useEffect, createContext, useState, useRef, ReactNode } from "react";
import { usePathname } from "next/navigation";
import {
  initAmplitude,
  isAmplitudeInitialized,
} from "@/services/AmplitudeService/AmplitudeService";
import { QRType } from "@/types/qr";
import { isStripeCriticalPath } from "@/internals/constants/paths";
import { IAmplitudeContext } from "./types";

export const AmplitudeContext = createContext<IAmplitudeContext | undefined>(undefined);

interface AmplitudeContextProviderProps {
  children: ReactNode;
}

export const AmplitudeContextProvider = ({ children }: AmplitudeContextProviderProps) => {
  const [source, setSource] = useState<string>("");
  const [qrSelected, setQrSelected] = useState<QRType | undefined>();
  const [data, setData] = useState<unknown>(undefined);

  const initializationRef = useRef<Promise<void> | null>(null);
  const idleTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const pathname = usePathname();

  useEffect(() => {
    if (isAmplitudeInitialized()) return;

    if (initializationRef.current) {
      void initializationRef.current.catch(() => undefined);
      return;
    }

    const runInit = async (): Promise<void> => {
      const shouldDefer = isStripeCriticalPath(pathname);

      if (shouldDefer) {
        // En rutas críticas (Stripe), esperar a idle
        await new Promise<void>((resolve) => {
          idleTimeoutRef.current = setTimeout(() => {
            idleTimeoutRef.current = null;
            resolve();
          }, 3000);

          // Usar requestIdleCallback si está disponible
          if (typeof window !== "undefined" && "requestIdleCallback" in window) {
            window.requestIdleCallback(() => {
              if (idleTimeoutRef.current) {
                clearTimeout(idleTimeoutRef.current);
                idleTimeoutRef.current = null;
              }
              resolve();
            });
          }
        });
      }
      await initAmplitude();
    };

    initializationRef.current = runInit().catch((error) => {
      if (process.env.NODE_ENV === "development") {
        console.debug("[AmplitudeContextProvider] Init error:", error);
      }
    });

    // Cleanup: cancelar timeouts pendientes al desmontar
    return () => {
      if (idleTimeoutRef.current) {
        clearTimeout(idleTimeoutRef.current);
        idleTimeoutRef.current = null;
      }
    };
  }, [pathname]);

  const value: IAmplitudeContext = {
    source,
    setSource,
    data,
    setData,
    qrSelected,
    setQrSelected,
  };

  return <AmplitudeContext.Provider value={value}>{children}</AmplitudeContext.Provider>;
};
